import React, { useState } from "react";
import { Box, BoxProps } from "theme-ui";
import { Variants } from "@kameleon-util/global-theme";
import { FormProps } from "../primary/form";

type FormType = Omit<BoxProps, "onSubmit"> &
  FormProps & {
    formClassName: string;
  };

const defaultProps = {
  // eslint-disable-next-line react/default-props-match-prop-types
  checkbox: undefined,
};

export const Form: React.FC<FormType> = ({
  inputs,
  checkbox,
  submitButton,
  onSubmit,
  submitMsgSuccess,
  submitMsgError,
  formClassName,
  className,
}) => {
  const [formValues, setFormValues] = useState({});
  const [checked, setChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasCheckbox = checkbox && (checkbox?.label ?? "").length > 0;

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    setIsSubmitting(true);
    onSubmit(
      Object.keys(formValues).map((key) => (formValues as any)?.[key]),
      (isSuccess) => {
        if (isSuccess) {
          setFormValues({});
          setChecked(false);
        }
        setIsSubmitting(false);
      }
    );
  };

  const handleChange = (value: string, index: number): void => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [index]: value,
    }));
  };

  return (
    <Box className={className}>
      <Box as="form" className={formClassName} onSubmit={handleSubmit}>
        {inputs?.map((input: Variants.InputFieldType, index: number) => {
          const InputComponent = Variants.InputFactory(input.type);
          const inputKey = `input-${input.name}-${index}`;
          return (
            <InputComponent
              key={inputKey}
              label={input.label}
              required={input.required}
              hover={input.hover}
              placeholder={input.placeholder}
              type={input.type}
              onChange={(value: string): void => handleChange(value, index)}
            />
          );
        })}
        <Box
          sx={{
            marginTop: 0,
            marginLeft: 0,
            marginRight: 0,
            gridColumn: "1 / span 2",
            "@media screen and (max-width: 767px)": {
              margin: "0.75rem",
            },
          }}
        >
          {hasCheckbox && (
            <Variants.Checkbox
              label={checkbox.label}
              linkLabel={checkbox.linkLabel}
              linkUrl={checkbox.linkUrl}
              checked={checked}
              onChange={(): void => setChecked(!checked)}
            />
          )}
        </Box>
        <Variants.ButtonSubmit
          value={submitButton}
          disabled={(hasCheckbox && !checked) || isSubmitting}
          id="submit"
        />
      </Box>
      {submitMsgError && <Variants.SubmitError message={submitMsgError} />}
      {submitMsgSuccess && (
        <Variants.SubmitSuccess message={submitMsgSuccess} />
      )}
    </Box>
  );
};

Form.defaultProps = defaultProps;
